<div class="td" role="dialog" aria-modal="true" [attr.aria-label]="dialogTitle()" (keydown.escape)="dismiss()">

  <!-- ── Floating close button (no separate header chrome — Notion-style)
       The type toggle moved to the properties panel since it's just
       another property of the task, not a header-level concept. -->
  <button class="td__close" (click)="dismiss()" [attr.aria-label]="'schedule.task_dialog.close' | transloco" type="button">
    <paper-icon name="close" [size]="14" aria-hidden="true" />
  </button>

  <!-- ── AI helper — "Help me write" ───────────────────────
       Click to expand a brief input. Calls TaskAiService on the
       backend (Haiku 4.5) which returns a draft we drop into the
       title + description + properties. User can edit anything
       afterwards; nothing about the draft is binding. -->
  <div *imFeatureFlag="'platform.tasks-ai'" class="td__ai" [class.td__ai--open]="aiOpen()">
    @if (!aiOpen()) {
      <button class="td__ai-trigger" (click)="aiOpen.set(true)" type="button"
              [attr.aria-label]="'schedule.task_dialog.help_me_write_a_task_with' | transloco">
        <span class="td__ai-spark" aria-hidden="true">
          <paper-icon name="sparkles" [size]="14" aria-hidden="true" />
        </span>
        <span>{{ 'schedule.task_dialog.help_me_write' | transloco }}</span>
        <span class="td__ai-hint">{{ 'schedule.task_dialog.describe_a_task_and_ai_will' | transloco }}</span>
      </button>
    } @else {
      <div class="td__ai-panel">
        <div class="td__ai-panel-head">
          <span class="td__ai-spark" aria-hidden="true">
            <paper-icon name="sparkles" [size]="14" aria-hidden="true" />
          </span>
          <span class="td__ai-panel-label">{{ 'schedule.task_dialog.describe_what_you_want_to_do' | transloco }}</span>
          <button class="td__ai-close" (click)="aiOpen.set(false); aiBrief.set('')"
                  type="button" [attr.aria-label]="'schedule.task_dialog.close_ai_helper' | transloco">
            <paper-icon name="close" [size]="12" aria-hidden="true" />
          </button>
        </div>
        <textarea class="td__ai-input"
                  [ngModel]="aiBrief()"
                  (ngModelChange)="aiBrief.set($event)"
                  [placeholder]="'schedule.task_dialog.e_g_send_q3_budget_review' | transloco"
                  rows="2"
                  [disabled]="aiBusy()"
                  (keydown.enter)="onAiBriefEnter($event)"
                  [attr.aria-label]="'schedule.task_dialog.ai_brief_input' | transloco"></textarea>
        @if (aiError()) {
          <div class="td__ai-error" role="alert">{{ aiError() }}</div>
        }
        <div class="td__ai-actions">
          <paper-button intent="ghost" size="sm" (click)="aiOpen.set(false); aiBrief.set('')">
            {{ 'schedule.task_dialog.cancel' | transloco }}
          </paper-button>
          <paper-button intent="ai" size="sm"
                     [disabled]="!aiBrief().trim() || aiBusy()"
                     [loading]="aiBusy()"
                     icon="arrow-right" iconPosition="right" [iconSize]="12"
                     (click)="onGenerate()">
            @if (aiBusy()) {
              {{ 'schedule.task_dialog.drafting' | transloco }}
            } @else {
              {{ 'schedule.task_dialog.generate' | transloco }}
            }
          </paper-button>
        </div>
      </div>
    }
  </div>

  <!-- ── Title — large, transparent input ─────────── -->
  <input class="td__title-input" type="text"
    [ngModel]="title()" (ngModelChange)="title.set($event)"
    [placeholder]="type() === 'focus' ? 'Focus block title' : 'Task title'"
    [disabled]="isEdit() && !canEditFields()"
    [attr.aria-readonly]="isEdit() && !canEditFields() ? 'true' : null"
    (keydown.enter)="save()" [attr.aria-label]="'schedule.task_dialog.task_title' | transloco" />

  <!-- ── Rich text editor ────────────────────────── -->
  <div class="td__editor-wrapper">
    <!-- Toolbar -->
    <div class="td__toolbar">
      <button class="tb-btn" [class.active]="isActive('bold')" (click)="toggleBold()" [title]="'schedule.task_dialog.bold' | transloco" type="button">
        <paper-icon name="bold" [size]="14" aria-hidden="true" />
      </button>
      <button class="tb-btn" [class.active]="isActive('italic')" (click)="toggleItalic()" [title]="'schedule.task_dialog.italic' | transloco" type="button">
        <paper-icon name="italic" [size]="14" aria-hidden="true" />
      </button>
      <button class="tb-btn" [class.active]="isActive('underline')" (click)="toggleUnderline()" [title]="'schedule.task_dialog.underline' | transloco" type="button">
        <paper-icon name="underline" [size]="14" aria-hidden="true" />
      </button>
      <span class="tb-sep"></span>
      <button class="tb-btn" [class.active]="isActive('heading', { level: 2 })" (click)="setHeading(2)" [title]="'schedule.task_dialog.heading' | transloco" type="button">H2</button>
      <button class="tb-btn" [class.active]="isActive('heading', { level: 3 })" (click)="setHeading(3)" [title]="'schedule.task_dialog.subheading' | transloco" type="button">H3</button>
      <span class="tb-sep"></span>
      <button class="tb-btn" [class.active]="isActive('bulletList')" (click)="toggleBulletList()" [title]="'schedule.task_dialog.bullet_list' | transloco" type="button">
        <paper-icon name="list" [size]="14" aria-hidden="true" />
      </button>
      <button class="tb-btn" [class.active]="isActive('orderedList')" (click)="toggleOrderedList()" [title]="'schedule.task_dialog.numbered_list' | transloco" type="button">
        <paper-icon name="list-ordered" [size]="14" aria-hidden="true" />
      </button>
    </div>

    <!-- Editor content area -->
    <div class="td__editor" #editorEl></div>
  </div>

  <!-- ── Properties panel — Notion-style label/value rows ── -->
  <div class="td__props">
    <!-- Type -->
    <div class="td__prop-row">
      <span class="td__prop-label">{{ 'schedule.task_dialog.type' | transloco }}</span>
      <div class="td__prop-value">
        <button class="td__type-pill" [class.active]="type() === 'task'"
                (click)="type.set('task')" type="button">
          <paper-icon name="check-circle" [size]="12" aria-hidden="true" />
          {{ 'schedule.task_dialog.task' | transloco }}
        </button>
        <button class="td__type-pill td__type-pill--focus" [class.active]="type() === 'focus'"
                (click)="type.set('focus')" type="button">
          <paper-icon name="target" [size]="12" aria-hidden="true" />
          {{ 'schedule.task_dialog.focus' | transloco }}
        </button>
      </div>
    </div>

    <!-- Priority — CDK overlay so the menu portals to the body and
         doesn't get clipped by the dialog's overflow:hidden. -->
    <div class="td__prop-row">
      <span class="td__prop-label">{{ 'schedule.task_dialog.priority' | transloco }}</span>
      <div class="td__prop-value">
        <button class="td__chip td__chip--priority"
                cdkOverlayOrigin
                #priorityOrigin="cdkOverlayOrigin"
                [attr.data-priority]="priority()"
                (click)="priorityDropdownOpen.update(v => !v); $event.stopPropagation()"
                type="button">
          <span class="td__priority-dot" [attr.data-priority]="priority()" aria-hidden="true"></span>
          {{ priorityLabel(priority()) }}
        </button>
        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="priorityOrigin"
          [cdkConnectedOverlayOpen]="priorityDropdownOpen()"
          [cdkConnectedOverlayPositions]="dropdownPositions"
          [cdkConnectedOverlayHasBackdrop]="false"
          cdkConnectedOverlayPanelClass="td-overlay-panel"
          (overlayOutsideClick)="priorityDropdownOpen.set(false)">
          <div class="td__chip-dropdown td__chip-dropdown--priority" (click)="$event.stopPropagation()">
            @for (p of priorities; track p) {
              <button class="td__chip-option" [class.active]="priority() === p"
                      (click)="selectPriority(p)" type="button">
                <span class="td__priority-dot" [attr.data-priority]="p" aria-hidden="true"></span>
                <span>{{ priorityLabel(p) }}</span>
              </button>
            }
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Date / when — CDK overlay portal so the calendar can render
         outside the dialog's overflow boundary (the previous absolute-
         positioned dropdown was being clipped by `.td { overflow:hidden }`).
         Quick rows for Today / Tomorrow / Next Monday sit at the top
         for one-click moves; the full calendar is below for further-
         out picks. (dateChanged) emits to the parent so busy blocks
         re-fetch and the availability strip mirrors the new day. -->
    <div class="td__prop-row">
      <span class="td__prop-label">{{ 'schedule.task_dialog.when' | transloco }}</span>
      <div class="td__prop-value">
        <button class="td__chip td__chip--when"
                cdkOverlayOrigin
                #whenOrigin="cdkOverlayOrigin"
                (click)="datePickerOpen.update(v => !v); $event.stopPropagation()"
                [attr.aria-expanded]="datePickerOpen()"
                [attr.aria-label]="'Change date — currently ' + dateLabel()"
                type="button">
          <paper-icon name="calendar" [size]="12" aria-hidden="true" />
          {{ dateLabel() }}
          <paper-icon class="td__chip-caret" name="chevron-down" [size]="10" aria-hidden="true" />
        </button>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="whenOrigin"
          [cdkConnectedOverlayOpen]="datePickerOpen()"
          [cdkConnectedOverlayPositions]="dropdownPositions"
          [cdkConnectedOverlayHasBackdrop]="false"
          cdkConnectedOverlayPanelClass="td-overlay-panel"
          (overlayOutsideClick)="datePickerOpen.set(false)">
          <div class="td__chip-dropdown td__date-dropdown" (click)="$event.stopPropagation()">
            <!-- Quick rows — most-used targets first. The full picker
                 below covers anything further out than next Monday. -->
            <div class="td__date-quick">
              <button class="td__date-quick-row" type="button" (click)="onQuickDate('today')">{{ 'schedule.task_dialog.today' | transloco }}</button>
              <button class="td__date-quick-row" type="button" (click)="onQuickDate('tomorrow')">{{ 'schedule.task_dialog.tomorrow' | transloco }}</button>
              <button class="td__date-quick-row" type="button" (click)="onQuickDate('next-monday')">{{ 'schedule.task_dialog.next_monday' | transloco }}</button>
            </div>
            <div class="td__date-quick-sep"></div>
            <dp-date-picker [selected]="effectiveDate()" (dateSelected)="onDateChange($event)" />
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Duration -->
    <div class="td__prop-row">
      <span class="td__prop-label">{{ 'schedule.task_dialog.duration' | transloco }}</span>
      <div class="td__prop-value td__duration-row">
        <span class="td__chip td__chip--readonly">
          <paper-icon name="clock" [size]="12" aria-hidden="true" />
          {{ durationLabel(duration()) }}
        </span>
        <input class="td__slider" type="range" [min]="15" [max]="maxDuration()" [step]="15"
          [ngModel]="duration()" (ngModelChange)="duration.set($event)"
          [attr.aria-label]="'Duration: ' + durationLabel(duration())" />
      </div>
    </div>

    <!-- Time slot — chip + day-availability strip ─────────────────
         The strip spans 7am–7pm and shows existing busy blocks
         (meetings / bookings / scheduled tasks) as muted bands. The
         user's selected time appears as a brand-coloured highlight,
         and clicking anywhere else on the strip snaps the start to
         that point. Hides cleanly when no busy data is provided. -->
    <div class="td__prop-row td__prop-row--time">
      <span class="td__prop-label">{{ 'schedule.task_dialog.time' | transloco }}</span>
      <div class="td__prop-value">
        @if (showTimeInputs()) {
          <span class="td__chip td__chip--time">
            <input class="td__time-inline" type="time"
              [ngModel]="startTimeStr()" (ngModelChange)="onStartTimeChange($event)" [attr.aria-label]="'schedule.task_dialog.start' | transloco" />
            <span class="td__time-sep">–</span>
            <input class="td__time-inline" type="time"
              [ngModel]="endTimeStr()" (ngModelChange)="onEndTimeChange($event)" [attr.aria-label]="'schedule.task_dialog.end' | transloco" />
          </span>
        } @else {
          <button class="td__chip td__chip--add" (click)="enableTimeInputs()" type="button">
            <paper-icon name="plus" [size]="12" aria-hidden="true" />
            {{ 'schedule.task_dialog.pick_a_time' | transloco }}
          </button>
        }
      </div>
    </div>

    @if (dayBusyBlocks().length > 0 || showTimeInputs()) {
      <div class="td__prop-row td__prop-row--strip">
        <span class="td__prop-label td__prop-label--secondary">{{ 'schedule.task_dialog.day' | transloco }}</span>
        <div class="td__prop-value td__strip-wrap">
          <button class="td__strip"
                  type="button"
                  (click)="onStripClick($event)"
                  [attr.aria-label]="'Day timeline — click to set start time'">
            <!-- Hour gridlines -->
            @for (h of stripHourMarks(); track h.label) {
              <span class="td__strip-grid" [style.left.%]="h.pct" aria-hidden="true"></span>
            }

            <!-- Busy blocks -->
            @for (b of stripBusyBlocks(); track $index) {
              <span class="td__strip-busy"
                    [style.left.%]="b.leftPct"
                    [style.width.%]="b.widthPct"
                    [title]="b.label"
                    aria-hidden="true"></span>
            }

            <!-- Selected slot -->
            @if (stripSelection(); as sel) {
              <span class="td__strip-selection"
                    [style.left.%]="sel.leftPct"
                    [style.width.%]="sel.widthPct"
                    aria-hidden="true"></span>
            }
          </button>

          <!-- Hour labels -->
          <div class="td__strip-labels" aria-hidden="true">
            @for (h of stripHourMarks(); track h.label) {
              <span class="td__strip-label" [style.left.%]="h.pct">{{ h.label }}</span>
            }
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Suggested slots (when no time set) -->
  @if (!showTimeInputs() && suggestedSlots().length > 0) {
    <div class="td__suggestions">
      @for (slot of suggestedSlots(); track slot.startMin) {
        <button class="td__slot-btn" (click)="selectSuggestedSlot(slot)" type="button">
          {{ formatTime(slot.startMin) }}–{{ formatTime(slot.endMin) }}
        </button>
      }
    </div>
  }

  <!-- Error -->
  @if (errorMsg()) {
    <div class="td__error" role="alert">{{ errorMsg() }}</div>
  }

  <!-- ── Restriction notice (external read-only tasks) ───────── -->
  @if (isEdit() && !canEditFields() && restrictionMessage(); as msg) {
    <div class="td__restriction" role="status" aria-live="polite">
      <span>{{ msg }}</span>
    </div>
  }

  <!-- ── Footer actions ──────────────────────────── -->
  <div class="td__footer">
    @if (isEdit() && canEditFields()) {
      <!-- Normal edit mode footer: complete / delete -->
      <div class="td__footer-left">
        @if (!isDone() && canCompleteTask()) {
          <paper-button intent="affirmative" size="sm" icon="check" [iconSize]="12" (click)="onComplete()">
            {{ 'schedule.task_dialog.complete' | transloco }}
          </paper-button>
        }
        @if (canDeleteTask()) {
          <paper-button intent="destructive" size="sm" icon="trash" [iconSize]="12" (click)="onDelete()">
            {{ 'schedule.task_dialog.delete' | transloco }}
          </paper-button>
        }
      </div>
    }
    <span class="td__spacer"></span>
    @if (isEdit() && !canEditFields()) {
      <!-- Restricted: single "Open in source" action, plus Cancel -->
      <paper-button intent="ghost" (click)="dismiss()">{{ 'schedule.task_dialog.close_2' | transloco }}</paper-button>
      <paper-button intent="primary" [disabled]="!deepLinkUrl()" (click)="openInSource()">
        {{ 'schedule.task_dialog.open_in_sourcelabel' | transloco:{ sourceLabel: sourceLabel() } }}
      </paper-button>
    } @else {
      <paper-button intent="ghost" (click)="dismiss()">{{ 'schedule.task_dialog.cancel_2' | transloco }}</paper-button>
      <paper-button [intent]="type() === 'focus' ? 'affirmative' : 'primary'"
                 [disabled]="!title().trim() || isSaving()"
                 [loading]="isSaving()"
                 (click)="save()">
        @if (isSaving()) { {{ 'schedule.task_dialog.saving' | transloco }} }
        @else if (isEdit()) { {{ 'schedule.task_dialog.save' | transloco }} }
        @else if (hasTimeSlot()) { {{ 'schedule.task_dialog.schedule' | transloco }} }
        @else { {{ 'schedule.task_dialog.add_to_day' | transloco }} }
      </paper-button>
    }
  </div>
</div>
